// Spreadsheet files for the product import (correction pass 2026-10-27, item 8), without a library: an .xlsx file is
// a zip of XML parts. Reading: the first worksheet's cells as text, read from the XML as written (a cell stored as
// text keeps its leading zeros; a cell stored as a number is reported, so a barcode turned into a number by Excel is
// caught instead of silently losing its zeros). Writing: the template, whose code and barcode columns are formatted
// as text, so values typed into them stay text. CSV is read as UTF-8 (with or without a byte-order mark).

export interface Sheet {
  rows: string[][]; /** Cells stored as numbers, "row:col" (1-based row), for the text columns' check. */ numeric: Set<string>;
  /** The workbook's other sheets by name (rows of text), e.g. the hidden settings sheet of an items export. */
  others?: Record<string, string[][]>;
}

// ---------------------------------------------------------------- CSV

export function parseCsv(text: string): Sheet {
  const t = text.replace(/^﻿/, "");
  // The separator is the first of , ; or tab found in the header line.
  const head = t.slice(0, t.search(/\r?\n|$/));
  const sep = [",", ";", "\t"].map((s) => [s, head.split(s).length] as const).sort((a, b) => b[1] - a[1])[0]![0];
  const rows: string[][] = [];
  let row: string[] = [], cell = "", q = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i]!;
    if (q) {
      if (ch === "\"") { if (t[i + 1] === "\"") { cell += "\""; i++; } else q = false; }
      else cell += ch;
    } else if (ch === "\"" && cell === "") q = true;
    else if (ch === sep) { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && t[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += ch;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return { rows: rows.filter((r) => r.some((c) => c.trim() !== "")), numeric: new Set() };
}

const csvCell = (s: string) => (/[",;\n\r]/.test(s) || /^[=+\-@]/.test(s) ? `"${(/^[=+\-@]/.test(s) ? "'" : "") + s.replace(/"/g, "\"\"")}"` : s);
/** CSV text with a byte-order mark, so Excel opens it as UTF-8 (Arabic included). Formula-like cells are neutralised. */
export const toCsv = (rows: string[][]) => "﻿" + rows.map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";

// ---------------------------------------------------------------- zip

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream("deflate-raw");
  const out = new Blob([data as BlobPart]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(out).arrayBuffer());
}

async function unzip(buf: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const b = new Uint8Array(buf), v = new DataView(buf), files = new Map<string, Uint8Array>();
  // The central directory, found from the end record (its comment may follow it).
  let end = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65_558); i--) if (v.getUint32(i, true) === 0x06054b50) { end = i; break; }
  if (end < 0) throw new Error("not_zip");
  const count = v.getUint16(end + 10, true);
  let p = v.getUint32(end + 16, true);
  const dec = new TextDecoder();
  for (let k = 0; k < count; k++) {
    if (v.getUint32(p, true) !== 0x02014b50) throw new Error("not_zip");
    const method = v.getUint16(p + 10, true), size = v.getUint32(p + 20, true);
    const nameLen = v.getUint16(p + 28, true), extraLen = v.getUint16(p + 30, true), commentLen = v.getUint16(p + 32, true), local = v.getUint32(p + 42, true);
    const name = dec.decode(b.subarray(p + 46, p + 46 + nameLen));
    const start = local + 30 + v.getUint16(local + 26, true) + v.getUint16(local + 28, true);
    const raw = b.subarray(start, start + size);
    if (method === 0) files.set(name, raw);
    else if (method === 8) files.set(name, await inflate(raw));
    p += 46 + nameLen + extraLen + commentLen;
  }
  return files;
}

const CRC = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; } return t; })();
const crc32 = (d: Uint8Array) => { let c = 0xffffffff; for (const x of d) c = CRC[(c ^ x) & 0xff]! ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; };

/** A zip of stored (uncompressed) files: small, and readable by Excel, LibreOffice and this reader. */
function zip(files: [string, string][]): Blob {
  const enc = new TextEncoder(), parts: Uint8Array[] = [], central: Uint8Array[] = [];
  let offset = 0;
  for (const [name, text] of files) {
    const n = enc.encode(name), d = enc.encode(text), crc = crc32(d);
    const h = new DataView(new ArrayBuffer(30));
    h.setUint32(0, 0x04034b50, true); h.setUint16(4, 20, true); h.setUint16(8, 0, true); h.setUint32(14, crc, true); h.setUint32(18, d.length, true); h.setUint32(22, d.length, true); h.setUint16(26, n.length, true);
    const c = new DataView(new ArrayBuffer(46));
    c.setUint32(0, 0x02014b50, true); c.setUint16(4, 20, true); c.setUint16(6, 20, true); c.setUint32(16, crc, true); c.setUint32(20, d.length, true); c.setUint32(24, d.length, true); c.setUint16(28, n.length, true); c.setUint32(42, offset, true);
    parts.push(new Uint8Array(h.buffer), n, d);
    central.push(new Uint8Array(c.buffer), n);
    offset += 30 + n.length + d.length;
  }
  const size = central.reduce((s, x) => s + x.length, 0);
  const e = new DataView(new ArrayBuffer(22));
  e.setUint32(0, 0x06054b50, true); e.setUint16(8, files.length, true); e.setUint16(10, files.length, true); e.setUint32(12, size, true); e.setUint32(16, offset, true);
  return new Blob([...parts, ...central, new Uint8Array(e.buffer)] as BlobPart[], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

// ---------------------------------------------------------------- xlsx

const colIndex = (ref: string) => { let n = 0; for (const ch of ref.replace(/\d+$/, "")) n = n * 26 + (ch.charCodeAt(0) - 64); return n - 1; };
const textOf = (el: Element | null) => (el ? [...el.getElementsByTagName("t")].map((t) => t.textContent ?? "").join("") : "");

export async function parseXlsx(buf: ArrayBuffer): Promise<Sheet> {
  const files = await unzip(buf);
  const dec = new TextDecoder();
  const xml = (name: string) => { const f = files.get(name); return f ? new DOMParser().parseFromString(dec.decode(f), "application/xml") : null; };
  const shared = [...(xml("xl/sharedStrings.xml")?.getElementsByTagName("si") ?? [])].map((si) => textOf(si));
  // The first sheet in the workbook's order holds the rows; the others are read as text by name.
  const wb = xml("xl/workbook.xml"), rels = xml("xl/_rels/workbook.xml.rels");
  const pathOf = (el: Element | undefined) => {
    const rid = el?.getAttribute("r:id") ?? el?.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id");
    const target = [...(rels?.getElementsByTagName("Relationship") ?? [])].find((r) => r.getAttribute("Id") === rid)?.getAttribute("Target");
    return target ? (target.startsWith("/") ? target.slice(1) : `xl/${target}`) : null;
  };
  const all = [...(wb?.getElementsByTagName("sheet") ?? [])];
  const sheet = xml(pathOf(all[0]) ?? "xl/worksheets/sheet1.xml") ?? xml("xl/worksheets/sheet1.xml");
  if (!sheet) throw new Error("no_sheet");
  const first = readRows(sheet, shared);
  const others: Record<string, string[][]> = {};
  for (const el of all.slice(1)) { const p = pathOf(el), d = p ? xml(p) : null; if (d) others[el.getAttribute("name") ?? ""] = readRows(d, shared).rows; }
  return { ...first, others };
}

function readRows(sheet: Document, shared: string[]): Sheet {
  const rows: string[][] = [], numeric = new Set<string>();
  for (const r of sheet.getElementsByTagName("row")) {
    const rn = Number(r.getAttribute("r"));
    const out: string[] = [];
    for (const c of r.getElementsByTagName("c")) {
      const i = colIndex(c.getAttribute("r") ?? "A1"), type = c.getAttribute("t");
      const v = c.getElementsByTagName("v")[0]?.textContent ?? "";
      const val = type === "s" ? shared[Number(v)] ?? "" : type === "inlineStr" ? textOf(c.getElementsByTagName("is")[0] ?? null) : type === "b" ? (v === "1" ? "yes" : "no") : v;
      if (!type || type === "n") { if (v !== "") numeric.add(`${rn}:${i}`); }
      while (out.length < i) out.push("");
      out[i] = val;
    }
    while (rows.length < rn - 1) rows.push([]);
    rows[rn - 1] = out;
  }
  return { rows, numeric };
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const colName = (i: number) => { let s = ""; for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s; return s; };

/**
 * An .xlsx workbook with one sheet: a bold header row and the rows given, every cell text. `textCols` are formatted
 * as text for the whole column (number format "@"), so what is typed there later stays text.
 */
export function buildXlsx(sheetName: string, rows: string[][], textCols: number[]): Blob {
  return buildWorkbook([{ name: sheetName, rows, textCols }]);
}

export interface WorkbookSheet {
  name: string;
  /** The first row is the header (bold, frozen); every cell is written as text. */
  rows: string[][];
  /** Columns formatted as text for the whole column, so what is typed there later stays text (leading zeros). */
  textCols?: number[];
  /** Columns hidden in the sheet (technical columns). */
  hiddenCols?: number[];
  /** Header cells shown as required (bold on a pale yellow fill). */
  requiredCols?: number[];
  /** Header cells of columns for reading only (bold on a grey fill). */
  readonlyCols?: number[];
  /** A drop-down list for a column, from the second row down. */
  lists?: { col: number; values: string[] }[];
  /** A sheet the user does not see (Excel: Unhide). */
  hidden?: boolean;
  /** Right to left (an Arabic sheet). */
  rtl?: boolean;
  /** Column width in characters (default 18). */
  widths?: Record<number, number>;
}

/** An .xlsx workbook of text sheets, written without a library (stored zip). Excel, LibreOffice and parseXlsx read it. */
export function buildWorkbook(sheets: WorkbookSheet[]): Blob {
  const NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main", RNS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
  const sheetXml = (sh: WorkbookSheet) => {
    const width = Math.max(1, ...sh.rows.map((r) => r.length));
    const cols = Array.from({ length: width }, (_, i) => `<col min="${i + 1}" max="${i + 1}" width="${sh.widths?.[i] ?? 18}"${sh.textCols?.includes(i) ? " style=\"1\"" : ""}${sh.hiddenCols?.includes(i) ? " hidden=\"1\"" : ""} customWidth="1"/>`).join("");
    const body = sh.rows.map((r, ri) => `<row r="${ri + 1}">${r.map((v, ci) => `<c r="${colName(ci)}${ri + 1}" t="inlineStr" s="${ri === 0 ? (sh.requiredCols?.includes(ci) ? 3 : sh.readonlyCols?.includes(ci) ? 4 : 2) : 1}"><is><t xml:space="preserve">${esc(v)}</t></is></c>`).join("")}</row>`).join("");
    // A list's values are written into the rule itself (at most 255 characters together), so no helper sheet is needed.
    const dv = (sh.lists ?? []).map((l) => `<dataValidation type="list" allowBlank="1" showErrorMessage="1" sqref="${colName(l.col)}2:${colName(l.col)}10000"><formula1>"${esc(l.values.join(",")).slice(0, 255)}"</formula1></dataValidation>`).join("");
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="${NS}"><sheetViews><sheetView workbookViewId="0"${sh.rtl ? " rightToLeft=\"1\"" : ""}><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><cols>${cols}</cols><sheetData>${body}</sheetData>${dv ? `<dataValidations count="${sh.lists!.length}">${dv}</dataValidations>` : ""}</worksheet>`;
  };
  const n = sheets.length;
  const overrides = sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("");
  const wbSheets = sheets.map((sh, i) => `<sheet name="${esc(sh.name.slice(0, 31))}" sheetId="${i + 1}"${sh.hidden ? " state=\"hidden\"" : ""} r:id="rId${i + 1}"/>`).join("");
  const wbRels = sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="${RNS}/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join("");
  return zip([
    ["[Content_Types].xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>${overrides}<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`],
    ["_rels/.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="${RNS}/officeDocument" Target="xl/workbook.xml"/></Relationships>`],
    ["xl/workbook.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="${NS}" xmlns:r="${RNS}"><bookViews><workbookView activeTab="0"/></bookViews><sheets>${wbSheets}</sheets></workbook>`],
    ["xl/_rels/workbook.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${wbRels}<Relationship Id="rId${n + 1}" Type="${RNS}/styles" Target="styles.xml"/></Relationships>`],
    ["xl/styles.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="${NS}"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="4"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFFF2CC"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFE7E6E6"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="5"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="49" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/><xf numFmtId="49" fontId="1" fillId="2" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1"/><xf numFmtId="49" fontId="1" fillId="3" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1"/></cellXfs></styleSheet>`],
    ...sheets.map((sh, i): [string, string] => [`xl/worksheets/sheet${i + 1}.xml`, sheetXml(sh)])
  ]);
}

/** SHA-256 of a file, hex. */
export async function digest(buf: ArrayBuffer): Promise<string> {
  return [...new Uint8Array(await crypto.subtle.digest("SHA-256", buf))].map((x) => x.toString(16).padStart(2, "0")).join("");
}
